import Text from '@axa-japan/design-system-react/Text';
import { ReactNode, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import crossIconUrl from '../../../assets/cross_icon.svg';

type Position = { x: number; y: number };

type FloatingPopupProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  containerRef: RefObject<HTMLElement>;
  children: ReactNode;
  width?: number;
};

const FloatingPopup = ({ open, title, onClose, containerRef, children, width = 560 }: FloatingPopupProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const initializedRef = useRef(false);

  const clamp = useCallback(
    (next: Position): Position => {
      const frame = containerRef.current;
      const popup = popupRef.current;
      if (!frame) return next;
      const frameW = frame.clientWidth;
      const frameH = frame.clientHeight;
      const popupH = popup?.offsetHeight ?? 0;
      const x = Math.min(Math.max(next.x, 0), Math.max(0, frameW - width));
      const y = Math.min(Math.max(next.y, 0), Math.max(0, frameH - popupH));
      return { x, y };
    },
    [containerRef, width],
  );

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setPosition(null);
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !containerRef.current || !popupRef.current) return;
    const frame = containerRef.current;
    const popup = popupRef.current;

    const anchorBottom = () => {
      const popupH = popup.offsetHeight;
      const y = Math.max(0, frame.clientHeight - popupH - 24);
      setPosition((prev) => {
        if (!initializedRef.current) {
          const x = Math.max(0, Math.floor((frame.clientWidth - width) / 2));
          initializedRef.current = true;
          return { x, y };
        }
        // Keep x as-is (user may have dragged); only re-anchor y to preserve the bottom edge.
        return prev ? { x: prev.x, y } : prev;
      });
    };

    anchorBottom();
    const observer = new ResizeObserver(anchorBottom);
    observer.observe(popup);
    return () => observer.disconnect();
  }, [open, containerRef, width]);

  if (!open) return null;

  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!position) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: position.x, origY: position.y };
  };
  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    setPosition((prev) => (prev ? clamp({ x: d.origX + (e.clientX - d.startX), y: d.origY + (e.clientY - d.startY) }) : prev));
  };
  const onDragEnd = () => {
    dragRef.current = null;
  };

  return (
    <div
      ref={popupRef}
      className="absolute bg-white rounded-sm shadow-lg z-20 flex flex-col overflow-hidden"
      style={{ left: position?.x ?? 0, top: position?.y ?? 0, width, visibility: position ? 'visible' : 'hidden' }}
    >
      <div
        className="flex items-center justify-between bg-interactiveHoverGrey px-2 py-1 cursor-move select-none touch-none"
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
      >
        <Text size="sm" weight="bold">
          {title}
        </Text>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="border-0 bg-transparent p-1 cursor-pointer"
          aria-label="閉じる"
        >
          <img className="w-4" src={crossIconUrl} alt="閉じる" />
        </button>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
};

export default FloatingPopup;
